import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
  ArrowLeft, 
  Download, 
  Printer,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';

// Utility function to format decimal values
const formatScore = (value) => {
  // console.log({value});
  if (value === undefined || value === null || value === '') return '-';

  if (value==0 || value==0.0 || value=='0' || value=='0.0') {
    return '-'
  }
  
  const num = parseFloat(value);
  if (isNaN(num)) return '-';
  
  // If the number ends with .0, show as integer
  if (num % 1 === 0) {
    return Math.floor(num);
  }
  
  // Otherwise, show as is
  return num;
};

// PDF Styles
const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 5,
    borderBottom: '1pt solid #1e40af',
    paddingBottom: 5,
  },
  logo: {
    width: 50,
    height: 50,
    margin: '0 auto 3',
  },
  schoolName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#1e40af',
    marginVertical: 3,
  },
  schoolInfo: {
    fontSize: 8,
    marginTop: 3,
  },
  table: {
    display: 'table',
    width: '100%',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    marginVertical: 5,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
  },
  tableCellLastColumn: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
  },
  tableCellLastRow: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
  },
  tableCellLastCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
  },
  // Fixed subject table cell styles with consistent widths
  subjectTableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    width: '6.67%',
  },
  subjectTableCellLastColumn: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    width: '6.67%',
  },
  subjectTableCellLastRow: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    width: '6.67%',
  },
  subjectTableCellLastCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    width: '6.67%',
  },
  subjectNameCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'left',
    width: '20%',
  },
  subjectNameCellLastRow: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'left',
    width: '20%',
  },
  tableHeaderCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    backgroundColor: '#1e40af',
    color: '#fff',
    fontWeight: 'bold',
    width: '6.67%',
  },
  tableHeaderCellLastColumn: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'center',
    backgroundColor: '#1e40af',
    color: '#fff',
    fontWeight: 'bold',
    width: '6.67%',
  },
  tableHeaderSubjectNameCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 2,
    fontSize: 7,
    textAlign: 'left',
    width: '20%',
    backgroundColor: '#1e40af',
    color: '#fff',
    fontWeight: 'bold',
  },
  // Psychomotor table styles with full width
  psychomotorTableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'left',
    width: '25%',
  },
  psychomotorTableCellLastColumn: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'left',
    width: '25%',
  },
  psychomotorTableCellLastRow: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'left',
    width: '25%',
  },
  psychomotorTableCellLastCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'left',
    width: '25%',
  },
  // Individual psychomotor skill boxes
  psychomotorSkillBox: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    padding: 2, //4 
    marginRight: 8,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  psychomotorSkillName: {
    fontSize: 8,
    fontWeight: 'bold',
    marginRight: 8,
  },
  psychomotorSkillValue: {
    fontSize: 8,
    textAlign: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#000',
    padding: 1, //2
    width: 20,
    height: 15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  psychomotorGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginVertical: 5,
  },
  // Full width table cell for key sections and comments
  fullWidthTableCell: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'left',
    width: '100%',
  },
  fullWidthTableCellLast: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: '#000',
    padding: 3,
    fontSize: 8,
    textAlign: 'left',
    width: '100%',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 5,
  },
  stamp: {
    textAlign: 'center',
    marginVertical: 0,
  },
  stampImage: {
    width: 60,
    margin: '0 auto',
  },
});

// PDF Document Component
const ResultPDF = ({ resultData, getTermlyPosition, formatRank }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Image src="/images/logo.jpg" style={styles.logo} />
        <Text style={styles.schoolName}>Unique & Fine International Schools, Enugu</Text>
        <Text style={styles.schoolInfo}>
          Postal Address: Trans-Aerodrome Emene (Beside Adoration Poultry), Enugu
        </Text>
        <Text style={styles.schoolInfo}>
          Email: info@uniqueandfineinternationalschools.com | Phone: 08156798185 | Website: www.uniqueandfineinternationalschools.com
        </Text>
      </View>

      {/* Student Information */}
      <View style={styles.table}>
        {[
          [
            { text: `NAME OF STUDENT: ${resultData?.student?.fullname || '-'}`, width: '33.33%' },
            { text: `CLASS: ${resultData?.class?.name || '-'}`, width: '33.33%' },
            { text: `NO. IN CLASS: ${resultData?.class?.total_students || '-'}`, width: '33.33%' },
          ],
          [
            { text: `ADMISSION NO: ${resultData?.student?.admission_no || '-'}`, width: '33.33%' },
            { text: `TERM: ${resultData?.term || '-'}`, width: '33.33%' },
            { text: `SESSION: ${resultData?.session || '-'}`, width: '33.33%' },
          ],
          [
            { text: `AVERAGE SCORE: ${resultData?.summary?.average_score || '-'}`, width: '33.33%' },
            { text: `TOTAL MARKS: ${resultData?.summary?.total_marks || '-'}`, width: '33.33%' },
            { text: `TOTAL OBTAINABLE: ${resultData?.summary?.total_obtainable_score || '-'}`, width: '33.33%' },
          ],
          [
            { text: `POSITION: ${getTermlyPosition(resultData?.term)}`, width: '33.33%' },
            { text: `SEX: ${''}`, width: '33.33%' },
            { text: `NEXT TERM BEGINS: ${resultData?.next_term_begins || '-'}`, width: '33.33%' },
          ],
        ].map((row, rowIndex, rowArray) => (
          <View style={styles.tableRow} key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <View
                style={[
                  rowIndex === rowArray.length - 1 && cellIndex === row.length - 1
                    ? styles.tableCellLastCell
                    : rowIndex === rowArray.length - 1
                    ? styles.tableCellLastRow
                    : cellIndex === row.length - 1
                    ? styles.tableCellLastColumn
                    : styles.tableCell,
                  { width: cell.width },
                ]}
                key={cellIndex}
              >
                <Text>{cell.text}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>

      {/* Subject Table */}
      <Text style={styles.sectionTitle}>REPORT SHEET FOR {resultData?.class?.category} SECONDARY</Text>
      <View style={styles.table}>
        <View style={[styles.tableRow, styles.tableHeader]}>
          <View style={styles.tableHeaderSubjectNameCell}>
            <Text>Subject</Text>
          </View>
          {[
            'Test1 (10%)',
            'Test2 (10%)',
            'Assign (10%)',
            'Project (10%)',
            'Exam (60%)',
            'Total Score',
            'Grade',
            'Remark',
            'Position',
            'Highest',
            'Lowest',
            'Average',
          ].map((header, index) => (
            <View
              style={index === 11 ? styles.tableHeaderCellLastColumn : styles.tableHeaderCell}
              key={index}
            >
              <Text>{header}</Text>
            </View>
          ))}
        </View>
        {resultData?.results?.map((subject, index, array) => (
          <View style={styles.tableRow} key={index}>
            <View style={index === array.length - 1 ? styles.subjectNameCellLastRow : styles.subjectNameCell}>
              <Text>{subject.short_name}</Text>
            </View>
            {[
              formatScore(subject.test1) || 0,
              formatScore(subject.test2) || 0,
              formatScore(subject.assignment) || 0,
              formatScore(subject.project) || 0,
              formatScore(subject.exam) || 0,
              formatScore(subject.total_score) || 0,
              subject.grade || '-',
              subject.remark == 'N/A' ? '-' : subject.remark,
              subject.position == '-' ? '-' : formatRank(subject.position),
              formatScore(subject?.evaluation?.highest_score) || '-',
              formatScore(subject?.evaluation?.lowest_score) || '-',
              formatScore(subject?.evaluation?.average) || '-',
            ].map((value, cellIndex) => (
              <View
                style={[
                  index === array.length - 1 && cellIndex === 11
                    ? styles.subjectTableCellLastCell
                    : index === array.length - 1
                    ? styles.subjectTableCellLastRow
                    : cellIndex === 11
                    ? styles.subjectTableCellLastColumn
                    : styles.subjectTableCell,
                ]}
                key={cellIndex}
              >
                <Text>{value}</Text>
              </View>
            ))}
          </View>
        ))}
      </View>

      {/* Psychomotor Evaluation */}
      <Text style={styles.sectionTitle}>Psychomotor Evaluation</Text>
      <View style={styles.psychomotorGrid}>
        {[
          { name: 'Punctuality', value: resultData?.psychomotor?.punctuality || 'N/A' },
          { name: 'Teamwork', value: resultData?.psychomotor?.teamwork || 'N/A' },
          { name: 'Creativity', value: resultData?.psychomotor?.creativity || 'N/A' },
          { name: 'Leadership Skills', value: resultData?.psychomotor?.leadership_skills || 'N/A' },
          { name: 'Handwriting', value: resultData?.psychomotor?.handwriting || 'N/A' },
          { name: 'Neatness', value: resultData?.psychomotor?.neatness || 'N/A' },
          { name: 'Participation in Activities', value: resultData?.psychomotor?.participation_in_activities || 'N/A' },
          { name: 'Sportsmanship', value: resultData?.psychomotor?.participation || 'N/A' },
          { name: 'Concentration', value: resultData?.psychomotor?.concentration || 'N/A' },
          { name: 'Problem-Solving Skills', value: resultData?.psychomotor?.problem_solving_skills || 'N/A' },
          { name: 'Self-Discipline', value: resultData?.psychomotor?.self_discipline || 'N/A' },
          { name: 'Respect for Others', value: resultData?.psychomotor?.respect_for_others || 'N/A' },
        ].map((item, index) => (
          <View style={styles.psychomotorSkillBox} key={index}>
            <Text style={styles.psychomotorSkillName}>{item.name}</Text>
            <View style={styles.psychomotorSkillValue}>
              <Text>{item.value}</Text>
            </View>
          </View>
        ))}
      </View>

      {/* Key Section */}
      <View style={styles.table}>
        {[
          'KEY TO GRADE: F = Fail (0 - 39.9) P = Pass (40 - 59.9) C = Good (60 - 69.9) B = Very Good (70 - 79.9) A = Excellent (80 - 100)',
          'KEY TO RATINGS: 1 = Very Poor 2 = Poor 3 = Fair 4 = Good 5 = Excellent',
        ].map((text, index) => (
          <View style={styles.tableRow} key={index}>
            <View style={index === 1 ? styles.fullWidthTableCellLast : styles.fullWidthTableCell}>
              <Text>{text}</Text>
            </View>
          </View>
        ))}
      </View>

      {/* Stamp */}
      <View style={styles.stamp}>
        <Image src="/images/stamp_optimized.png" style={styles.stampImage} />
      </View>

      {/* Comments */}
      <View style={styles.table}>
        {[
          `Form Teacher's Comment: ${resultData?.comment?.form_teacher_comments || 'No comment available'}`,
          `Principal's Comment: ${resultData?.comment?.principal_comments || 'No comment available'}`,
        ].map((text, index) => (
          <View style={styles.tableRow} key={index}>
            <View style={index === 1 ? styles.fullWidthTableCellLast : styles.fullWidthTableCell}>
              <Text>{text}</Text>
            </View>
          </View>
        ))}
      </View>
    </Page>
  </Document>
);

const StudentSingleResult = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  const admission_no = queryParams.get('admission_no') || 'admission no';
  const studentUuid = queryParams.get('studentUuid');
  const className = queryParams.get('className');
  const session = queryParams.get('session');
  const term = queryParams.get('term');

  const navigate = useNavigate();
  
  const { user } = useSelector((state) => state.auth);
  const { teacherData } = useSelector((state) => state.teacherData || {});

  // Find student from Redux data
  const student = teacherData?.students?.find(s => s.uuid === studentUuid);

  // Fetch result data
  const resultUrl = `${API_BASE_URL}/api/web/result/student-summary-data/${studentUuid}/${className}`;
  const { data: fetchResult, loading, error } = useFetch(resultUrl);

  // const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  // Helper function to format rank
  const formatRank = (position) => {
    if (!position) return 'N/A';
    const lastDigit = position % 10;
    const lastTwoDigits = position % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return `${position}th`;
    }
    
    switch (lastDigit) {
      case 1: return `${position}st`;
      case 2: return `${position}nd`;
      case 3: return `${position}rd`;
      default: return `${position}th`;
    }
  };

  // Helper function for remark styling
  const getRemarkClass = (remark) => {
    switch(remark?.toLowerCase()) {
      case 'excellent': return 'text-green-600 font-bold';
      case 'good': return 'text-blue-600 font-bold';
      case 'credit': return 'text-blue-500 font-bold';
      case 'pass': return 'text-yellow-600 font-bold';
      case 'fail': return 'text-red-600 font-bold';
      default: return 'text-gray-600 font-bold';
    }
  };

  // Get term position
  const getTermlyPosition = (term) => {
    if (!fetchResult?.data?.termPositions) return 'N/A';
    
    switch(term?.toLowerCase()) {
      case 'first': return formatRank(fetchResult.data.termPositions.first_term_position);
      case 'second': return formatRank(fetchResult.data.termPositions.second_term_position);
      case 'third': return formatRank(fetchResult.data.termPositions.third_term_position);
      default: return 'N/A';
    }
  };

  // Handle print functionality
  const handlePrint = () => {
    window.print();
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading result information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !fetchResult?.success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-4 rounded-lg">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              Error loading result. Please try again.
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>
    );
  }

  const resultData = fetchResult?.data;

  // console.log({results: resultData?.results});
  

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Student Result
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {resultData?.student?.fullname} - {resultData?.class?.name}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              {/* <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print
              </button> */}
              <PDFDownloadLink
                document={<ResultPDF resultData={resultData} getTermlyPosition={getTermlyPosition} formatRank={formatRank} />}
                fileName={`student_result_${resultData?.student?.fullname}.pdf`}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                {({ loading }) => (
                  <>
                    {loading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {loading ? 'Generating...' : 'Download PDF'}
                  </>
                )}
              </PDFDownloadLink>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 text-xs">
          <div className="border border-gray-400 p-3" id="print-content">
            {/* School Header */}
            <div className="text-center mb-3 pb-3 border-b-2 border-blue-800">
              <div className="mx-auto w-16 h-16">
                <img src="/images/logo.jpg" alt="School Logo" className="w-full h-full" />
              </div>
              <h1 className="text-lg font-bold text-blue-600 my-2">Unique & Fine International Schools, Enugu</h1>
              <div className="flex justify-between text-xs mt-2 flex-col md:flex-row">
                <div>
                  <strong>Postal Address:</strong><br />
                  Trans-Aerodrome Emene (Beside Adoration Poultry)<br />
                  Enugu
                </div>
                <div>
                  <strong>Email:</strong> info@uniqueandfineinternationalschools.com<br />
                  <strong>Phone:</strong> 08156798185<br />
                  <strong>Website:</strong> www.uniqueandfineinternationalschools.com
                </div>
              </div>
            </div>

            {/* Student Information */}
            <table className="w-full border border-gray-800 mb-3 text-xs border-collapse">
              <tbody>
                {[
                  [
                    <><strong>NAME OF STUDENT:</strong> {resultData?.student?.fullname}</>,
                    <><strong>CLASS:</strong> {resultData?.class?.name}</>,
                    <><strong>NO. IN CLASS:</strong> {resultData?.class?.total_students}</>,
                  ],
                  [
                    <><strong>ADMISSION NO:</strong> {resultData?.student?.admission_no}</>,
                    <><strong>TERM:</strong> {resultData?.term}</>,
                    <><strong>SESSION:</strong> {resultData?.session}</>,
                  ],
                  [
                    <><strong>AVERAGE SCORE:</strong> {resultData?.summary?.average_score}</>,
                    <><strong>TOTAL MARKS:</strong> {resultData?.summary?.total_marks}</>,
                    <><strong>TOTAL OBTAINABLE:</strong> {resultData?.summary?.total_obtainable_score}</>,
                  ],
                  [
                    <><strong>POSITION:</strong> {getTermlyPosition(resultData?.term)}</>,
                    <><strong>SEX:</strong> {resultData?.student?.gender}</>,
                    <><strong>NEXT TERM BEGINS:</strong> {resultData?.next_term_begins}</>,
                  ],
                ].map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td
                        className={`border-r border-b border-gray-800 p-1 ${cellIndex === 2 ? 'border-r-0' : ''} ${rowIndex === 3 ? 'border-b-0' : ''}`}
                        key={cellIndex}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Subject Table */}
            <h3 className="text-center font-bold my-3 text-sm">REPORT SHEET FOR {resultData?.class?.category} SECONDARY</h3>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-800 mb-3 text-xs table-fixed border-collapse">
                <thead>
                  <tr className="bg-blue-600 text-white">
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-left w-[20%]">Subject</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Tests<br/>(10%)</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Tests<br/>(10%)</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Assign<br/>(10%)</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Project<br/>(10%)</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Exam<br/>(60%)</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Total<br/>Score</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Grade</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Remark</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Position</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Highest<br/>Score</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Lowest<br/>Score</th>
                    <th className="border-r border-b border-gray-800 p-1 font-medium text-xs text-center w-[6.67%]">Average</th>
                  </tr>
                </thead>
                <tbody>
                  {resultData?.results?.map((subject, index, array) => (
                    <tr key={index}>
                      <td className={`border-r border-b border-gray-800 p-1 text-left ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {subject?.short_name}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject.test1)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject.test2)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject.assignment)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject.project)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject.exam)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject.total_score)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {subject.grade}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${getRemarkClass(subject.remark)} ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {subject.remark == 'N/A' ? '-' : subject.remark}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {subject.position == '-' ? '-' : formatRank(subject.position)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject?.evaluation?.highest_score)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject?.evaluation?.lowest_score)}
                      </td>
                      <td className={`border-r border-b border-gray-800 p-1 text-center ${index === array.length - 1 ? 'border-b-0' : ''}`}>
                        {formatScore(subject?.evaluation?.average)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Psychomotor Evaluation */}
            <h3 className="font-bold my-3 text-sm">Psychomotor Evaluation</h3>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {[
                { name: 'Punctuality', value: resultData?.psychomotor?.punctuality || 'N/A' },
                { name: 'Teamwork', value: resultData?.psychomotor?.teamwork || 'N/A' },
                { name: 'Creativity', value: resultData?.psychomotor?.creativity || 'N/A' },
                { name: 'Leadership Skills', value: resultData?.psychomotor?.leadership_skills || 'N/A' },
                { name: 'Handwriting', value: resultData?.psychomotor?.handwriting || 'N/A' },
                { name: 'Neatness', value: resultData?.psychomotor?.neatness || 'N/A' },
                { name: 'Participation in Activities', value: resultData?.psychomotor?.participation_in_activities || 'N/A' },
                { name: 'Sportsmanship', value: resultData?.psychomotor?.participation || 'N/A' },
                { name: 'Concentration', value: resultData?.psychomotor?.concentration || 'N/A' },
                { name: 'Problem-Solving Skills', value: resultData?.psychomotor?.problem_solving_skills || 'N/A' },
                { name: 'Self-Discipline', value: resultData?.psychomotor?.self_discipline || 'N/A' },
                { name: 'Respect for Others', value: resultData?.psychomotor?.respect_for_others || 'N/A' },
              ].map((item, index) => (
                <div key={index} className="border border-gray-800 p-2 flex items-center mr-2">
                  <span className="text-xs font-medium mr-2">{item.name}</span>
                  <div className="border border-gray-800 w-6 h-6 flex items-center justify-center text-xs">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Key Section */}
            <div className="text-xs mt-3">
              <table className="w-full border border-gray-800 text-xs border-collapse">
                <tbody>
                  {[
                    'KEY TO GRADE: F = Fail (0 - 39.9) P = Pass (40 - 59.9) C = Good (60 - 69.9) B = Very Good (70 - 79.9) A = Excellent (80 - 100)',
                    'KEY TO RATINGS: 1 = Very Poor 2 = Poor 3 = Fair 4 = Good 5 = Excellent',
                  ].map((text, index) => (
                    <tr key={index}>
                      <td className={`border-b border-gray-800 p-1 ${index === 1 ? 'border-b-0' : ''}`}>
                        <strong>{text}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Stamp and Comments */}
            <div className="text-xs mt-0">
              <div className="flex justify-center items-center">
                <img src="/images/stamp_optimized.png" alt="School Stamp" style={{ width: 80 }} />
              </div>
              <table className="w-full border border-gray-800 text-xs mt-0 border-collapse">
                <tbody>
                  {[
                    `Form Teacher's Comment: ${resultData?.comment?.form_teacher_comments || 'No comment available'}`,
                    `Principal's Comment: ${resultData?.comment?.principal_comments || 'No comment available'}`,
                  ].map((text, index) => (
                    <tr key={index}>
                      <td className={`border-b border-gray-800 p-1 ${index === 1 ? 'border-b-0' : ''}`}>
                        <strong>{text}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          @media print {
            @page {
              size: A4;
              margin: 15mm;
            }
            body {
              margin: 0;
              padding: 0;
              font-size: 10pt;
              font-family: Helvetica, sans-serif;
            }
            .print\\:hidden {
              display: none !important;
            }
            .max-w-4xl {
              max-width: 100% !important;
              width: 100% !important;
            }
            .p-4 {
              padding: 0 !important;
            }
            .shadow-sm {
              box-shadow: none !important;
            }
            .rounded-lg {
              border-radius: 0 !important;
            }
            .text-xs {
              font-size: 10pt !important;
            }
            .text-sm {
              font-size: 12pt !important;
            }
            .text-lg {
              font-size: 14pt !important;
            }
            .w-16 {
              width: 50px !important;
            }
            .h-16 {
              height: 50px !important;
            }
            .mb-3 {
              margin-bottom: 8pt !important;
            }
            .my-3 {
              margin-top: 8pt !important;
              margin-bottom: 8pt !important;
            }
            .mt-4 {
              margin-top: 10pt !important;
            }
            .mt-3 {
              margin-top: 8pt !important;
            }
            .p-3 {
              padding: 6pt !important;
            }
            .p-1 {
              padding: 3pt !important;
            }
            .border-2 {
              border-width: 1pt !important;
            }
            table {
              width: 100% !important;
              table-layout: fixed;
              border-collapse: collapse !important;
            }
            img[alt="School Stamp"] {
              width: 60px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default StudentSingleResult;